/**
 * Supervisor insights dashboard — historical analytics over a [from, to] window.
 *
 *   GET /insights?from=YYYY-MM-DD&to=YYYY-MM-DD   (supervisor / admin)
 *
 * Grows by section: Insights.1 ships KPIs + the weekly throughput series;
 * performance, mix, and utilization sections extend this response.
 */
import { z } from 'zod';
const DayStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const InsightsQuerySchema = z.object({
    from: DayStringSchema,
    to: DayStringSchema,
});
/** Headline counters for the window. */
export const InsightsKpisSchema = z.object({
    projects_completed: z.number().int().nonnegative(),
    tasks_completed: z.number().int().nonnegative(),
    crew_hours: z.number().nonnegative(),
    photos: z.number().int().nonnegative(),
    videos: z.number().int().nonnegative(),
    notes: z.number().int().nonnegative(),
    punch_found: z.number().int().nonnegative(),
    punch_closed: z.number().int().nonnegative(),
});
/** One bucket of the weekly throughput trend (week start = Monday). */
export const InsightsThroughputPointSchema = z.object({
    week_start: DayStringSchema,
    projects_completed: z.number().int().nonnegative(),
    install: z.number().int().nonnegative(),
    pickup: z.number().int().nonnegative(),
    punch: z.number().int().nonnegative(),
    labor: z.number().int().nonnegative(),
});
/** Performance & quality signals over the window (Insights.2). */
export const InsightsPerformanceSchema = z.object({
    /** Completed-by-deadline rate (0..1), or null if no projects had a requested window. */
    on_time_rate: z.number().nullable(),
    on_time_denominator: z.number().int().nonnegative(),
    /** Avg drive time (en route → on site), minutes. */
    avg_drive_minutes: z.number().nullable(),
    /** Avg on-site work (on site → complete), minutes. */
    avg_onsite_minutes: z.number().nullable(),
    /** Estimated vs actual labor (person-hours). */
    estimated_hours: z.number().nonnegative(),
    actual_hours: z.number().nonnegative(),
    /** Budget vs actual cost (cents) for completed projects. */
    budget_cents: z.number().int().nonnegative(),
    actual_cost_cents: z.number().int().nonnegative(),
    /** Rework. */
    installs_completed: z.number().int().nonnegative(),
    punch_per_install: z.number().nullable(),
    punch_blocking: z.number().int().nonnegative(),
});
// ---- Budget vs actual (VCS-602) --------------------------------------------
/**
 * Per-project margin row — "budgeted X, your team came in at Y". Actual labor
 * is derived from the status timeline (worked minutes × crew × rate), the same
 * actuals as GET /projects/:id/labor.
 */
export const InsightsBudgetRowSchema = z.object({
    project_id: z.string(),
    title: z.string(),
    client_name: z.string(),
    completed_at: z.string().nullable(),
    budget_cents: z.number().int().nullable(),
    estimated_labor_cents: z.number().int().nonnegative(),
    actual_labor_cents: z.number().int().nonnegative(),
    actual_person_hours: z.number().nonnegative(),
    /** budget − actual labor; negative = over budget. Null when no budget set. */
    variance_cents: z.number().int().nullable(),
});
// ---- Work mix + utilization (Insights.3) ----------------------------------
export const InsightsCountSchema = z.object({
    key: z.string(),
    count: z.number().int().nonnegative(),
});
export const InsightsMixSchema = z.object({
    by_kind: z.array(InsightsCountSchema), // completed projects by kind
    by_status: z.array(InsightsCountSchema), // ALL projects by current status (pipeline)
    by_task_type: z.array(InsightsCountSchema), // completed tasks by type
    top_clients: z.array(InsightsCountSchema), // completed projects by client
    by_pm: z.array(InsightsCountSchema), // completed projects by PM
});
export const InsightsCrewUtilSchema = z.object({
    name: z.string(),
    hours: z.number().nonnegative(),
    tasks: z.number().int().nonnegative(),
});
export const InsightsUtilizationSchema = z.object({
    crew: z.array(InsightsCrewUtilSchema),
});
// ---- Fleet analytics ------------------------------------------------------
/** Per-vehicle activity: trips + service in the window, open issues as of now. */
export const InsightsFleetVehicleSchema = z.object({
    label: z.string(),
    type: z.string(),
    status: z.string(),
    trips: z.number().int().nonnegative(), // completed tasks in the window
    service_count: z.number().int().nonnegative(), // service-log entries in the window
    service_cost_cents: z.number().int().nonnegative(),
    open_issues: z.number().int().nonnegative(), // currently open (not windowed)
});
export const InsightsFleetSchema = z.object({
    vehicles: z.array(InsightsFleetVehicleSchema),
    service_cost_cents: z.number().int().nonnegative(), // window total
    service_count: z.number().int().nonnegative(),
    open_issues: z.number().int().nonnegative(),
});
/** Email the report (Insights.4). Recipient defaults to the requesting user. */
export const InsightsReportEmailSchema = z.object({
    to: z.string().email().optional(),
});
export const InsightsReportEmailResultSchema = z.object({
    sent: z.boolean(),
    stub: z.boolean(),
    to: z.string(),
});
export const InsightsResponseSchema = z.object({
    from: DayStringSchema,
    to: DayStringSchema,
    kpis: InsightsKpisSchema,
    throughput: z.array(InsightsThroughputPointSchema),
    performance: InsightsPerformanceSchema,
    /** Per-project budget vs actual labor for the window's completed projects. */
    budget_vs_actual: z.array(InsightsBudgetRowSchema),
    mix: InsightsMixSchema,
    utilization: InsightsUtilizationSchema,
    fleet: InsightsFleetSchema,
});
//# sourceMappingURL=insights.js.map